import React from "react";

import Form from "antd/es/form";
import useFormInstance from "antd/es/form/hooks/useFormInstance";
import Radio from "antd/es/radio";
import { defineMessages, useIntl } from "react-intl";

import RadioGroup from "@app/component/input/RadioGroup";
import DuplicateMergeAccessValue from "@app/enum/DuplicateMergeAccessValue";
import MergeDuplicatesSettingValue from "@app/enum/MergeDuplicatesSettingValue";
import OrganizationSetting from "@app/enum/OrganizationSetting";

import styles from "./DuplicateRecords.module.scss";

const messages = defineMessages({
  description: {
    id: "settings.organizationDetails.data.duplicateRecords.description",
    defaultMessage: "Note: Owners can <i>always</i> check across all records.",
    description: "Settings organization details - Data - Duplicate Records - description",
  },
  organization: {
    id: "settings.organizationDetails.data.duplicateRecords.check.organization",
    defaultMessage: "All records in the organization",
    description: "Settings organization details - Merge Duplicates - organization",
  },
  orgDescription: {
    id: "settings.organizationDetails.data.duplicateRecords.check.organization.description",
    defaultMessage: "Check the entire org for duplicates, regardless of visibility.",
    description: "Settings organization details - Merge Duplicates - user",
  },
  team: {
    id: "settings.organizationDetails.data.duplicateRecords.check.team",
    defaultMessage: "Only records in that {allUsers, select, true {user} other {manager}}’s team",
    description: "Settings organization details - Merge Duplicates - team",
  },
  teamDescription: {
    id: "settings.organizationDetails.data.duplicateRecords.check.team.description",
    defaultMessage:
      "When a {allUsers, select, true {user} other {manager}} merges duplicates, only records from their team are checked.",
    description: "Settings organization details - Merge Duplicates - user",
  },
  title: {
    id: "settings.organizationDetails.data.duplicateRecords.title",
    defaultMessage: "Check for duplicates across:",
    description: "Settings organization details - Data - Duplicate Records - Title",
  },
  user: {
    id: "settings.organizationDetails.data.duplicateRecords.check.user",
    defaultMessage: "Only records the {allUsers, select, true {user} other {manager}} can see",
    description: "Settings organization details - Merge Duplicates - user",
  },
  userDescription: {
    id: "settings.organizationDetails.data.duplicateRecords.check.user.description",
    defaultMessage:
      "Only check records that are visible to that {allUsers, select, true {user} other {manager}}.",
    description: "Settings organization details - Merge Duplicates - user",
  },
});

interface Props {
  onChange?: (value: MergeDuplicatesSettingValue) => void;
  value?: MergeDuplicatesSettingValue;
}

const DuplicateRecords: React.FC<Props> = ({ onChange, value }) => {
  const intl = useIntl();
  const form = useFormInstance();
  const duplicateMergeAccess = Form.useWatch(OrganizationSetting.DUPLICATE_MERGE_ACCESS, form);

  return (
    <div className={styles.container}>
      <div>
        <div className={styles.description}>{intl.formatMessage(messages.title)}</div>
        <div className={styles.note}>
          {intl.formatMessage(messages.description, { i: (text) => <em>{text}</em> })}
        </div>
      </div>
      <RadioGroup<MergeDuplicatesSettingValue> onChange={onChange} size="small" value={value}>
        <div className={styles.options}>
          <Radio value={MergeDuplicatesSettingValue.USER}>
            <div className={styles.option}>
              <span>
                {intl.formatMessage(messages.user, {
                  allUsers: duplicateMergeAccess === DuplicateMergeAccessValue.ALL_USERS,
                  b: (text) => <span className={styles.bold}>{text}</span>,
                })}
              </span>
              <span className={styles.description}>
                {intl.formatMessage(messages.userDescription, {
                  allUsers: duplicateMergeAccess === DuplicateMergeAccessValue.ALL_USERS,
                })}
              </span>
            </div>
          </Radio>
          <Radio value={MergeDuplicatesSettingValue.TEAM}>
            <div className={styles.option}>
              <span>
                {intl.formatMessage(messages.team, {
                  allUsers: duplicateMergeAccess === DuplicateMergeAccessValue.ALL_USERS,
                  b: (text) => <span className={styles.bold}>{text}</span>,
                })}
              </span>
              <span className={styles.description}>
                {intl.formatMessage(messages.teamDescription, {
                  allUsers: duplicateMergeAccess === DuplicateMergeAccessValue.ALL_USERS,
                })}
              </span>
            </div>
          </Radio>
          <Radio value={MergeDuplicatesSettingValue.ORGANIZATION}>
            <div className={styles.option}>
              <span>
                {intl.formatMessage(messages.organization, {
                  b: (text) => <span className={styles.bold}>{text}</span>,
                })}
              </span>
              <span className={styles.description}>
                {intl.formatMessage(messages.orgDescription)}
              </span>
            </div>
          </Radio>
        </div>
      </RadioGroup>
    </div>
  );
};
export default DuplicateRecords;
